import { useCallback, useState, type FC } from "react";
import { Controller, useForm } from "react-hook-form";
import styled from "styled-components";

import { useRootDispatch } from "@ds/hooks";

import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { BooleanTriStateCheckbox, Checkbox, InputNumber } from "@ds/components/forms/inputs";
import { LabelWrapper } from "@ds/components/forms/labels";
import { MultipleValuesLabel } from "@ds/components/forms/labels/common";
import { IconType } from "@ds/components/icons";
import { Dialog } from "@ds/components/modals";

import { campaignItemsActions } from "../../redux/slice";

const ContentWrapper = styled.div`
  min-height: 100px;
  width: 420px;
`;

const FooterWrapper = styled.div`
  display: flex;
  justify-content: right;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const UpdateCampaignItemModal: FC<{
  campaignItems: CampaignItem[];
  campaign: Campaign;
  allItems: CampaignItem[];
  onClose: () => void;
}> = ({ campaignItems, campaign, allItems, onClose }) => {
  const isInterleaved = campaign.mixing_strategy === "interleaved";
  const isCampaignSingleItem = allItems.length === 1;
  const isMultiSelect = campaignItems.length > 1;
  const maxWeight = Math.max(1, 100 - (allItems.length - 1));
  const firstItem = campaignItems[0];

  // Tracks whether the user has explicitly interacted with the randomize checkbox from its
  // indeterminate ("Multiple Values") initial state, so unchecking (false) is still treated as
  // a deliberate change even though false matches the form defaultValue.
  const [randomizeTouched, setRandomizeTouched] = useState(false);

  const isWeightMultiple = isMultiSelect && new Set(campaignItems.map(i => i.weight)).size > 1;
  const isRandomizeMultiple = isMultiSelect && new Set(campaignItems.map(i => i.settings?.randomize ?? false)).size > 1;
  const isMinGapMultiple = isMultiSelect && new Set(campaignItems.map(i => i.settings?.min_gap ?? 0)).size > 1;
  const isMaxConsecutiveMultiple =
    isMultiSelect && new Set(campaignItems.map(i => i.settings?.max_consecutive ?? 0)).size > 1;

  const {
    control,
    getValues,
    handleSubmit,
    formState: { dirtyFields },
  } = useForm<CampaignItem>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      ...firstItem,
      weight: isCampaignSingleItem ? 100 : isWeightMultiple ? undefined : firstItem.weight,
      settings: {
        randomize: isRandomizeMultiple ? false : (firstItem.settings?.randomize ?? false),
        min_gap: isMinGapMultiple ? undefined : (firstItem.settings?.min_gap ?? 0),
        max_consecutive: isMaxConsecutiveMultiple ? undefined : (firstItem.settings?.max_consecutive ?? 0),
      },
    },
  });

  const dispatch = useRootDispatch();
  const onSubmitHandler = useCallback(() => {
    const values = getValues();

    if (!isMultiSelect) {
      // Single item — existing behavior with weight redistribution via saga
      const changedFields: Partial<CampaignItem> = {};
      if (isCampaignSingleItem && firstItem.weight !== 100) {
        changedFields.weight = 100;
      } else if (dirtyFields.weight) {
        changedFields.weight = values.weight;
      }

      if (isInterleaved && dirtyFields.settings) {
        changedFields.settings = {
          randomize: values.settings?.randomize ?? false,
          min_gap: values.settings?.min_gap ?? 0,
          max_consecutive: values.settings?.max_consecutive ?? 0,
        };
      }

      if (Object.keys(changedFields).length > 0) {
        dispatch(campaignItemsActions.updateCampaignItems([firstItem.id], changedFields));
      }
    } else {
      // Multiple items — per-item updates preserving "Multiple Values" fields
      const perItemUpdates = campaignItems
        .map(item => {
          const fields: Partial<CampaignItem> = {};

          if (dirtyFields.weight) {
            fields.weight = values.weight;
          }

          if (isInterleaved && (dirtyFields.settings || randomizeTouched)) {
            fields.settings = {
              randomize:
                dirtyFields.settings?.randomize || randomizeTouched
                  ? (values.settings?.randomize ?? false)
                  : (item.settings?.randomize ?? false),
              min_gap: dirtyFields.settings?.min_gap ? (values.settings?.min_gap ?? 0) : (item.settings?.min_gap ?? 0),
              max_consecutive: dirtyFields.settings?.max_consecutive
                ? (values.settings?.max_consecutive ?? 0)
                : (item.settings?.max_consecutive ?? 0),
            };
          }

          return { id: item.id, fields };
        })
        .filter(u => Object.keys(u.fields).length > 0);

      if (perItemUpdates.length > 0) {
        dispatch(campaignItemsActions.batchUpdateCampaignItemWeights(perItemUpdates));
      }
    }

    onClose();
  }, [
    dispatch,
    campaignItems,
    firstItem,
    getValues,
    dirtyFields,
    isInterleaved,
    isCampaignSingleItem,
    isMultiSelect,
    randomizeTouched,
    onClose,
  ]);

  const renderFooter = useCallback(
    () => (
      <FooterWrapper>
        <Button
          label="Cancel"
          ariaLabel="cancel button"
          buttonType={ButtonType.Outlined}
          actionType={ButtonActionType.Secondary}
          icon={IconType.Close}
          iconPosition="right"
          onClick={onClose}
        />
        <Button
          label="Update"
          ariaLabel="update button"
          buttonType={ButtonType.Raised}
          icon={IconType.Save}
          iconPosition="right"
          isDisabled={Object.keys(dirtyFields).length === 0 && !randomizeTouched}
          onClick={handleSubmit(onSubmitHandler)}
        />
      </FooterWrapper>
    ),
    [onClose, handleSubmit, onSubmitHandler, dirtyFields, randomizeTouched],
  );

  const header = isMultiSelect ? `Edit ${campaignItems.length} campaign items` : "Edit campaign item";

  return (
    <Dialog modal visible header={header} footer={renderFooter} onHide={onClose}>
      <ContentWrapper>
        <Controller
          name="weight"
          control={control}
          render={({ field: { ref, value, onChange }, fieldState }) => (
            <LabelWrapper label="Weight (%)" className="col-8" error={fieldState.error} isRequired>
              <InputNumber
                inputRef={ref}
                value={isWeightMultiple ? (value ?? undefined) : Number(value ?? 1)}
                min={1}
                max={maxWeight}
                suffix="%"
                placeholder={isWeightMultiple ? "Multiple Values" : ""}
                isDisabled={isCampaignSingleItem}
                onChange={({ value: v }) => onChange(isWeightMultiple ? (v ?? undefined) : (v ?? 1))}
              />
            </LabelWrapper>
          )}
        />

        {isInterleaved && (
          <>
            <Controller
              name="settings.randomize"
              control={control}
              render={({ field: { value, onChange } }) => (
                <LabelWrapper label="Randomize" className="col-8">
                  <CheckboxRow>
                    {isRandomizeMultiple && !randomizeTouched ? (
                      <BooleanTriStateCheckbox
                        isChecked={null}
                        onChange={() => {
                          setRandomizeTouched(true);
                          onChange(false);
                        }}
                      />
                    ) : (
                      <Checkbox checked={!!value} onChange={e => onChange(e.checked)} />
                    )}
                    {isRandomizeMultiple && !randomizeTouched && <MultipleValuesLabel />}
                  </CheckboxRow>
                </LabelWrapper>
              )}
            />

            <Controller
              name="settings.min_gap"
              control={control}
              render={({ field: { ref, value, onChange }, fieldState }) => (
                <LabelWrapper label="Min Gap" className="col-8" error={fieldState.error}>
                  <InputNumber
                    inputRef={ref}
                    value={isMinGapMultiple ? (value ?? undefined) : Number(value ?? 0)}
                    min={0}
                    placeholder={isMinGapMultiple ? "Multiple Values" : ""}
                    onChange={({ value: v }) => onChange(isMinGapMultiple ? (v ?? undefined) : (v ?? 0))}
                  />
                </LabelWrapper>
              )}
            />

            <Controller
              name="settings.max_consecutive"
              control={control}
              render={({ field: { ref, value, onChange }, fieldState }) => (
                <LabelWrapper label="Max Consecutive" className="col-8" error={fieldState.error}>
                  <InputNumber
                    inputRef={ref}
                    value={isMaxConsecutiveMultiple ? (value ?? undefined) : Number(value ?? 0)}
                    min={0}
                    placeholder={isMaxConsecutiveMultiple ? "Multiple Values" : ""}
                    onChange={({ value: v }) => onChange(isMaxConsecutiveMultiple ? (v ?? undefined) : (v ?? 0))}
                  />
                </LabelWrapper>
              )}
            />
          </>
        )}
      </ContentWrapper>
    </Dialog>
  );
};
